import { IonContent, IonLoading, IonModal } from '@ionic/react';
import React, { useCallback, useRef } from 'react';

import { Logo, Padding, ProgressBar } from '@/components/common';
import { Hours, Name, Services } from '@/components/profiles/addDentist';
import { AddDentistProvider, useAddDentist } from '@/contexts';
import { ADD_DENTIST_STEPS } from '@/utils/constants';
import { arrowBackOutline } from 'ionicons/icons';

import './AddDentistModal.css';

interface AddDentistModalProps {
  isOpen: boolean;
  onClose: () => void;
  presentingElement?: HTMLElement | undefined;
}

interface AddDentistModalContentProps {
  onClose: () => void;
  modalRef: React.RefObject<HTMLElement>;
}

const AddDentistModalContent: React.FC<AddDentistModalContentProps> = ({
  onClose,
  modalRef
}) => {
  const { loading, currentStep, onReset } = useAddDentist();

  const onBack = useCallback(() => {
    if (currentStep === ADD_DENTIST_STEPS.NAME) {
      onClose();
    } else {
      onReset();
    }
  }, [currentStep, onReset, onClose]);

  const renderAddDentist = () => {
    const totalSteps = Object.keys(ADD_DENTIST_STEPS).length;
    const currentStepIndex =
      Object.values(ADD_DENTIST_STEPS).indexOf(currentStep);

    const stepComponents = {
      [ADD_DENTIST_STEPS.NAME]: <Name />,
      [ADD_DENTIST_STEPS.HOURS]: <Hours />,
      [ADD_DENTIST_STEPS.SERVICES]: (
        <Services
          // TODO: presentingElement is temporarily set to undefined due to an issue with Ionic modal card state when used with IonTabs.
          // Uncomment the line below and remove presentingElement={undefined} once the issue is resolved.
          // presentingElement={modalRef.current}
          presentingElement={undefined}
        />
      )
    };

    return (
      <>
        <ProgressBar
          totalSteps={totalSteps}
          currentProgress={currentStepIndex}
        />
        {stepComponents[currentStep]}
      </>
    );
  };

  return (
    <IonContent>
      <Padding>
        <Logo
          leftIcon={
            currentStep === ADD_DENTIST_STEPS.SERVICES ? null : arrowBackOutline
          }
          onClickLeftIcon={onBack}
        />
        {renderAddDentist()}
      </Padding>

      <IonLoading isOpen={loading} message="Loading..." spinner="crescent" />
    </IonContent>
  );
};

const AddDentistModal: React.FC<AddDentistModalProps> = ({
  isOpen,
  onClose,
  presentingElement
}) => {
  const modal = useRef<HTMLIonModalElement | null>(null);
  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      presentingElement={presentingElement}
      ref={modal}
    >
      <AddDentistProvider>
        <AddDentistModalContent onClose={onClose} modalRef={modal} />
      </AddDentistProvider>
    </IonModal>
  );
};

export default AddDentistModal;
